import { useMemo } from 'react'
import { store, useAppDispatch, useAppSelector } from '#app/store'
import { setSelectedGroup } from '#store/slices/localConfigSlice'
import type { EnhancedMarket } from '#types/EnhancedMarket'

export const useFilteredMarkets = (markets: EnhancedMarket[]) => {
  const { selectedUsages, hiddenUsages, showAllUsagesGroup } = useAppSelector((x) => x.localConfig.marketFilter)

  const { availableUsages, marketsByUsage } = useMemo(() => {
    const availableUsages = new Set<string>()
    const hiddenUsagesAsSet = new Set<string>(hiddenUsages)
    const marketsByUsage = new Map<string, EnhancedMarket[]>()

    for (const market of markets) {
      const plannedUsages = market.plannedUsage ?? []
      for (const usage of plannedUsages) {
        if (hiddenUsagesAsSet.has(usage)) {
          continue
        }

        availableUsages.add(usage)
        if (!marketsByUsage.has(usage)) {
          marketsByUsage.set(usage, [])
        }
        marketsByUsage.get(usage)?.push(market)
      }
    }

    return {
      availableUsages,
      marketsByUsage,
    }
  }, [markets, hiddenUsages])

  const filteredMarkets = useMemo(() => {
    const selectedUsagesSet = new Set<string>(selectedUsages)

    if (selectedUsagesSet.size === 0 || selectedUsagesSet.has('__all__')) {
      return markets
    }

    return markets.filter((market) => {
      const plannedUsages = market.plannedUsage ?? []
      for (const usage of plannedUsages) {
        if (selectedUsagesSet.has(usage)) {
          return true
        }
      }
      return false
    })
  }, [selectedUsages, markets])

  const dispatch = useAppDispatch()

  const functions = useMemo(() => {
    return {
      setSelectedUsage: (usage?: string) => {
        dispatch(setSelectedGroup(usage ? [usage] : []))
      },
      setSelectedUsages: (usages: Set<string>) => {
        dispatch(setSelectedGroup(Array.from(usages)))
      },
      toggleSelectedUsage: (usage: string) => {
        const current = store.getState().localConfig.marketFilter.selectedUsages
        const asSet = new Set(current)
        if (asSet.has(usage)) {
          asSet.delete(usage)
        } else {
          asSet.add(usage)
        }
        dispatch(setSelectedGroup(Array.from(asSet)))
      },
    }
  }, [dispatch])

  return {
    showAllUsagesGroup,
    filteredMarkets,
    availableUsages,
    selectedUsages,
    // for single selection
    selectedUsage: Array.from(selectedUsages)[0],
    marketsByUsage,
    ...functions,
  }
}
